/*
 * Tells apart the three things that can register with notabox-server:
 * full edge controllers, mobile clients, and cloud anchors.
 *
 * The Android tunnel client has no api_url. It answers a read-only part of the
 * edge API over the reverse channel it dials (/healthz, /wans, /health,
 * /diagnostics, /cloud/anchors), so the console reads it through the same
 * proxy as an edge, and the `phone` gate in navigation.js keeps every screen
 * that needs more off it. Anchors register with an even thinner payload and
 * are not fleet members at all.
 */

/*
 * Cloud anchors register with notabox-server too, but only to publish the
 * identity key edges pin for tunnel encryption — they are infrastructure, not
 * fleet members. The server already omits them from /api/v1/nodes; this is the
 * client-side guard for a dashboard running against an older server, which
 * would otherwise render the anchor as a node.
 */
export function isAnchorNode(node) {
  return !!node && (node.role || "").toLowerCase() === "anchor";
}

export function isMobileNode(node) {
  if (!node) return false;
  // An explicit role always wins over any inference below.
  if (isAnchorNode(node)) return false;
  if ((node.role || "").toLowerCase() === "mobile") return true;
  // Everything below is for clients that predate the declared role: they said
  // nothing about themselves, so the id and the missing api_url are all there
  // is to go on. Both are unreliable, which is why the role exists.
  const id = (node.node_id || "").toLowerCase();
  if (id.startsWith("android-") || id.startsWith("ios-")) return true;
  // Direct-mode edges are full edge controllers reached via the reverse-tunnel
  // (not api_url) — so the "no api_url" heuristic below would mis-classify
  // them as mobile. mode wins.
  if ((node.mode || "").toLowerCase() === "direct") return false;
  // Last resort. The Android client's node_id is operator-editable, so it does
  // not reliably carry the android- prefix, and "registered with no api_url"
  // is the only other signal it gives. Anything that is genuinely NOT an edge
  // must therefore declare a role above rather than reach this line.
  if (!node.api_url || String(node.api_url).trim() === "") return true;
  return false;
}

export function nodeKindLabel(node) {
  return isMobileNode(node) ? "Mobile Client" : "Edge Controller";
}
